import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CHAT_FUNCTION } from '@/lib/chatFunction';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${session.access_token}`,
    };
  }
  return { "Content-Type": "application/json" };
}

export interface ProjectFile {
  name: string;
  content: string;
}

export interface Version {
  id: string;
  versionNumber: number;
  description: string;
  createdAt: string;
  files?: ProjectFile[];
}

export interface VersionHistoryState {
  versions: Version[];
  currentVersionIndex: number;
  isLoading: boolean;
  error: string | null;
}

export const useVersionHistory = () => {
  const [state, setState] = useState<VersionHistoryState>({
    versions: [],
    currentVersionIndex: -1,
    isLoading: false,
    error: null,
  });

  // Load versions from backend
  const loadVersions = useCallback(async (projectId: string) => {
    if (!projectId) return [];

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${SUPABASE_URL}/functions/v1/${CHAT_FUNCTION}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ action: "listVersions", projectId }),
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}`);
      }

      const data = await response.json();

      // Sort versions by version number ascending (oldest first = V1)
      const versions: Version[] = (data.versions || [])
        .map((v: any) => ({
          id: v.id,
          versionNumber: v.versionNumber,
          description: v.description,
          createdAt: v.createdAt,
        }))
        .sort((a: Version, b: Version) => a.versionNumber - b.versionNumber);

      setState({
        versions,
        currentVersionIndex: versions.length - 1,
        isLoading: false,
        error: null,
      });

      return versions;
    } catch (error) {
      console.error("Failed to load versions:", error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Erreur de chargement",
      }));
      return [];
    }
  }, []);

  // Create a new version
  const createVersion = useCallback(async (
    projectId: string,
    files: ProjectFile[],
    description?: string
  ): Promise<Version | null> => {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${SUPABASE_URL}/functions/v1/${CHAT_FUNCTION}`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          action: "createVersion",
          projectId,
          files,
          description
        }),
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}`);
      }

      const data = await response.json();

      const newVersion: Version = {
        id: data.id,
        versionNumber: data.versionNumber,
        description: data.description,
        createdAt: data.createdAt,
      };

      // Add to local state
      setState((prev) => {
        const newVersions = [...prev.versions, newVersion];
        return {
          ...prev,
          versions: newVersions,
          currentVersionIndex: newVersions.length - 1,
        };
      });

      return newVersion;
    } catch (error) {
      console.error("Failed to create version:", error);
      return null;
    }
  }, []);

  // Load a specific version's files
  const loadVersionFiles = useCallback(async (projectId: string, versionId: string): Promise<ProjectFile[]> => {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${SUPABASE_URL}/functions/v1/${CHAT_FUNCTION}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ action: "getVersionFiles", projectId, versionId }),
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}`);
      }

      const data = await response.json();
      return (data.files || []).map((f: any) => ({
        name: f.name,
        content: f.content,
      }));
    } catch (error) {
      console.error("Failed to load version files:", error);
      return [];
    }
  }, []);

  // Rollback to a specific version
  const rollbackVersion = useCallback(async (
    projectId: string,
    versionId: string
  ): Promise<{ files: ProjectFile[]; version: Version } | null> => {
    try {
      const headers = await getAuthHeaders();

      const response = await fetch(`${SUPABASE_URL}/functions/v1/${CHAT_FUNCTION}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ action: "rollbackVersion", projectId, versionId }),
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}`);
      }

      const data = await response.json();

      const newVersion: Version = {
        id: data.id,
        versionNumber: data.versionNumber,
        description: data.description,
        createdAt: data.createdAt,
      };

      // Add the new version to local state
      setState((prev) => {
        const newVersions = [...prev.versions, newVersion];
        return {
          ...prev,
          versions: newVersions,
          currentVersionIndex: newVersions.length - 1,
        };
      });

      return {
        files: data.files || [],
        version: newVersion,
      };
    } catch (error) {
      console.error("Failed to rollback version:", error);
      return null;
    }
  }, []);

  const goToVersion = useCallback((index: number) => {
    setState((prev) => {
      if (index < 0 || index >= prev.versions.length) return prev;
      return { ...prev, currentVersionIndex: index };
    });
  }, []);

  const goToPrevious = useCallback(() => {
    setState((prev) => {
      if (prev.currentVersionIndex <= 0) return prev;
      return { ...prev, currentVersionIndex: prev.currentVersionIndex - 1 };
    });
  }, []);

  const goToNext = useCallback(() => {
    setState((prev) => {
      if (prev.currentVersionIndex >= prev.versions.length - 1) return prev;
      return { ...prev, currentVersionIndex: prev.currentVersionIndex + 1 };
    });
  }, []);

  const goToLatest = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentVersionIndex: prev.versions.length - 1,
    }));
  }, []);

  const resetHistory = useCallback(() => {
    setState({
      versions: [],
      currentVersionIndex: -1,
      isLoading: false,
      error: null,
    });
  }, []);

  // Add a version locally (for real-time updates during streaming)
  const addLocalVersion = useCallback((version: Version) => {
    setState((prev) => {
      // Avoid duplicates
      if (prev.versions.some(v => v.id === version.id)) {
        return prev;
      }
      const newVersions = [...prev.versions, version];
      return {
        ...prev,
        versions: newVersions,
        currentVersionIndex: newVersions.length - 1,
      };
    });
  }, []);

  const currentVersion = state.versions[state.currentVersionIndex] || null;
  const canGoBack = state.currentVersionIndex > 0;
  const canGoForward = state.currentVersionIndex < state.versions.length - 1;
  const isAtLatest = state.currentVersionIndex === state.versions.length - 1;

  return {
    versions: state.versions,
    currentVersion,
    currentVersionIndex: state.currentVersionIndex,
    canGoBack,
    canGoForward,
    isAtLatest,
    isLoading: state.isLoading,
    error: state.error,
    loadVersions,
    loadVersionFiles,
    createVersion,
    rollbackVersion,
    goToVersion,
    goToPrevious,
    goToNext,
    goToLatest,
    resetHistory,
    addLocalVersion,
  };
};
